import React from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RealgemsSidebar from "./RealgemsSidebar";
import Topbar from "../menu/Topbar";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import { isRealgemsAdmin, normalizeAccessType } from "../../utils/accessControl";
import "./RealgemsOrders.css";

const PAGE_SIZE = 50;

const EMPTY_FORM = {
  realgemsClientId: "",
  orderNotes: "",
};

const ORDER_STATUS_META = {
  PENDING: { label: "Aguardando analise", className: "pending" },
  IN_PROGRESS: { label: "Em andamento", className: "in-progress" },
  COMPLETED: { label: "Concluido", className: "completed" },
  CANCELED: { label: "Cancelado", className: "canceled" },
};

const tableCellSx = {
  borderBottom: "1px solid #eef2f7",
  color: "#334155",
  fontSize: "0.88rem",
  py: 1.55,
  whiteSpace: "nowrap",
};

const headCellSx = {
  borderBottom: "1px solid #e2e8f0",
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  backgroundColor: "#f8fafc",
};

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const shortenText = (value, maxLen = 140) => {
  const text = String(value || "").trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 3)}...`;
};

const formatCnpj = (value = "") => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 14);

  if (!digits) return "-";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const getStatusMeta = (status) => ORDER_STATUS_META[status] || ORDER_STATUS_META.PENDING;

const RealgemsOrders = () => {
  const { user } = useAuth();
  const [menuClose, setMenuClose] = React.useState(false);
  const [orders, setOrders] = React.useState([]);
  const [approvedClients, setApprovedClients] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  const isAdminViewer = isRealgemsAdmin(normalizeAccessType(user?.access_type));

  const loadPageData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [ordersResponse, clientsResponse] = await Promise.all([
        Api.get("/realgems/orders"),
        Api.get("/realgems/clients"),
      ]);

      const orderItems = Array.isArray(ordersResponse?.data?.items) ? ordersResponse.data.items : [];
      const clientItems = Array.isArray(clientsResponse?.data?.items) ? clientsResponse.data.items : [];
      const approvedOnly = clientItems
        .filter((item) => String(item?.approvalStatus || "").toUpperCase() === "APPROVED")
        .sort((a, b) => String(a.tradeName || "").localeCompare(String(b.tradeName || "")));

      setOrders(orderItems);
      setApprovedClients(approvedOnly);
    } catch (err) {
      console.error("Erro ao carregar pedidos Realgems:", err);
      setError(err?.response?.data?.message || "Nao foi possivel carregar os pedidos Realgems.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
    resetMessages();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      resetMessages();

      const response = await Api.post("/realgems/orders", {
        realgemsClientId: form.realgemsClientId,
        orderNotes: form.orderNotes,
      });

      const savedItem = response?.data?.item;
      if (savedItem) {
        setOrders((current) => [savedItem, ...current]);
      } else {
        await loadPageData();
      }

      setSuccess("Pedido cadastrado com sucesso.");
      setForm(EMPTY_FORM);
      setIsCreateOpen(false);
    } catch (err) {
      console.error("Erro ao salvar pedido Realgems:", err);
      setError(err?.response?.data?.message || "Nao foi possivel salvar o pedido.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const query = normalizeSearchText(search);
    if (!query) return true;

    const statusMeta = getStatusMeta(order.status);
    const searchableText = normalizeSearchText(
      [
        order.clientTradeName,
        order.clientCompanyLegalName,
        order.clientCnpj,
        order.ownerUserName,
        order.orderNotes,
        order.adminNotes,
        statusMeta.label,
      ].join(" ")
    );

    return searchableText.includes(query);
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedOrders = filteredOrders.slice(
    (safeCurrentPage - 1) * PAGE_SIZE,
    safeCurrentPage * PAGE_SIZE
  );

  const approvedClientsCount = approvedClients.length;

  return (
    <div className="app-container realgems-orders-page">
      <RealgemsSidebar close={menuClose} />

      <main className="main-content realgems-orders-main-content">
        <Topbar page="Pedidos Realgems" onToggleMenu={() => setMenuClose((current) => !current)} />

        <section className="realgems-orders-stack">
          <article className="realgems-orders-panel realgems-orders-create-panel">
            <div className="realgems-orders-panel-header">
              <div>
                <span className="realgems-orders-kicker">Pedidos</span>
                <h1>{isAdminViewer ? "Acompanhamento de pedidos" : "Cadastro de pedidos"}</h1>
                <p>
                  {isAdminViewer
                    ? "Visualize todos os pedidos cadastrados pelos usuarios Realgems. A tratativa administrativa pode ser conectada depois sem perder o historico salvo."
                    : "Selecione um cliente aprovado e descreva no campo abaixo tudo o que deve entrar no pedido."}
                </p>
              </div>

              {!isAdminViewer ? (
                <button
                  type="button"
                  className="realgems-orders-primary-btn"
                  onClick={() => {
                    setIsCreateOpen((current) => !current);
                    resetMessages();
                  }}
                  disabled={approvedClientsCount === 0}
                >
                  {isCreateOpen ? "Fechar pedido" : "Novo pedido"}
                </button>
              ) : null}
            </div>

            {!isAdminViewer ? (
              isCreateOpen ? (
                <form className="realgems-orders-form" onSubmit={handleSubmit}>
                  <label className="realgems-orders-field">
                    <span>Cliente aprovado</span>
                    <select
                      value={form.realgemsClientId}
                      onChange={handleChange("realgemsClientId")}
                      disabled={approvedClientsCount === 0}
                    >
                      <option value="">Selecione um cliente</option>
                      {approvedClients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.tradeName} - {formatCnpj(client.cnpj)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="realgems-orders-field realgems-orders-field-wide">
                    <span>Observacao do pedido</span>
                    <textarea
                      value={form.orderNotes}
                      onChange={handleChange("orderNotes")}
                      placeholder="Ex: quantidades, fragrancias, itens desejados, prazo, prioridade e qualquer detalhe importante para este cliente."
                      rows={6}
                    />
                  </label>

                  <div className="realgems-orders-actions">
                    <button type="submit" disabled={submitting || approvedClientsCount === 0}>
                      {submitting ? "Salvando..." : "Salvar pedido"}
                    </button>
                  </div>

                  {error ? <div className="realgems-orders-message error">{error}</div> : null}
                  {success ? <div className="realgems-orders-message success">{success}</div> : null}
                </form>
              ) : (
                <>
                  <div className="realgems-orders-collapsed">
                    <div className="realgems-orders-collapsed-copy">
                      {approvedClientsCount > 0
                        ? `${approvedClientsCount} cliente(s) aprovado(s) disponivel(is) para receber novos pedidos.`
                        : "Voce ainda nao possui clientes aprovados para cadastrar pedidos."}
                    </div>
                  </div>

                  {error ? <div className="realgems-orders-message error">{error}</div> : null}
                  {success ? <div className="realgems-orders-message success">{success}</div> : null}
                </>
              )
            ) : (
              <div className="realgems-orders-collapsed">
                <div className="realgems-orders-collapsed-copy">
                  {loading
                    ? "Carregando pedidos..."
                    : `${filteredOrders.length} pedido(s) encontrado(s) de ${orders.length} salvo(s) no portal Realgems.`}
                </div>
              </div>
            )}
          </article>

          <article className="realgems-orders-panel">
            <div className="realgems-orders-panel-header compact">
              <div>
                <span className="realgems-orders-kicker">Lista</span>
                <h2>Pedidos cadastrados</h2>
                <p>
                  {loading
                    ? "Carregando..."
                    : isAdminViewer
                    ? `${filteredOrders.length} pedido(s) encontrado(s) de ${orders.length} salvo(s) por todos os usuarios Realgems.`
                    : `${filteredOrders.length} pedido(s) encontrado(s) de ${orders.length} salvo(s) no seu usuario.`}
                </p>
              </div>
            </div>

            <div className="realgems-orders-toolbar">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por cliente, CNPJ, usuario, status ou texto do pedido"
                className="realgems-orders-search"
              />
            </div>

            {error && !isCreateOpen ? <div className="realgems-orders-message error">{error}</div> : null}

            {loading ? (
              <div className="realgems-orders-empty-state">Carregando pedidos...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="realgems-orders-empty-state">
                {orders.length === 0 ? (
                  isAdminViewer ? (
                    <>Nenhum pedido Realgems cadastrado ainda.</>
                  ) : (
                    <>Nenhum pedido cadastrado ainda. Abra <strong>Novo pedido</strong> para registrar o primeiro.</>
                  )
                ) : (
                  <>Nenhum pedido encontrado com esse filtro.</>
                )}
              </div>
            ) : (
              <>
                <TableContainer component={Paper} className="realgems-orders-table-wrap">
                  <Table sx={{ minWidth: isAdminViewer ? 1060 : 920 }} aria-label="Tabela de pedidos Realgems">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Cliente</TableCell>
                        <TableCell sx={headCellSx}>CNPJ</TableCell>
                        {isAdminViewer ? <TableCell sx={headCellSx}>Cadastrado por</TableCell> : null}
                        <TableCell sx={headCellSx}>Status</TableCell>
                        <TableCell sx={headCellSx}>Pedido</TableCell>
                        <TableCell sx={headCellSx}>Data</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedOrders.map((order) => {
                        const statusMeta = getStatusMeta(order.status);

                        return (
                          <TableRow key={order.id} hover>
                            <TableCell sx={tableCellSx}>
                              <strong className="realgems-orders-table-strong">{order.clientTradeName}</strong>
                            </TableCell>
                            <TableCell sx={tableCellSx}>{formatCnpj(order.clientCnpj)}</TableCell>
                            {isAdminViewer ? (
                              <TableCell sx={tableCellSx}>{order.ownerUserName || "-"}</TableCell>
                            ) : null}
                            <TableCell sx={tableCellSx}>
                              <Chip
                                label={statusMeta.label}
                                size="small"
                                className={`realgems-order-status-badge ${statusMeta.className}`}
                              />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, whiteSpace: "normal", minWidth: 340 }}>
                              {shortenText(order.orderNotes, 180)}
                            </TableCell>
                            <TableCell sx={tableCellSx}>{formatDateTime(order.createdAt)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 ? (
                  <div className="realgems-orders-pagination">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safeCurrentPage === 1}
                    >
                      Anterior
                    </button>

                    <span>
                      Pagina {safeCurrentPage} de {totalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={safeCurrentPage === totalPages}
                    >
                      Proxima
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default RealgemsOrders;
